<template>
  <div class="col-lg-12 col-md-12 col-sm-12" v-if="chartVisible">
    <div class="card">
      <div class="header">{{ sex }} PFL Chart</div>
      <div class="body">
        <v-chart ref="chart" class="chart" :option="pflChart" autoresize />
      </div>
    </div>
  </div>
</template>

<script>
import VChart from "vue-echarts";
import crossUrl from "@/assets/cross.png";

export default {
  components: { "v-chart": VChart },
  props: {
    sex: { type: String, required: true },
    pflAnalysisResult: { type: Object, required: false },
  },
  data() {
    return {
      chartVisible: false,
      curves: { ages: [], mean: [], p10: [], p25: [], p75: [], p90: [] },
      pflChart: null,
      pendingPoints: [],
    };
  },
  methods: {
    async loadPFLCurves() {
      const fasdBackendURL =
        localStorage.getItem("fasdBackendURL") || "http://localhost:8000";
      const token = localStorage.getItem("userJWtoken");

      const q = new URLSearchParams({
        sex: (this.sex || "").toLowerCase(),
        from_age: "3",
        to_age: "16",
        step: "0.5",
      }).toString();

      const res = await fetch(`${fasdBackendURL}/api/PFL/PFLcurves?${q}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to load PFL curves: ${res.status}`);

      const data = await res.json();
      this.curves = data;
      this.pflChart = this.buildOptions(data);
      this.chartVisible = true;

      this.$nextTick(() => {
        this.$refs.chart?.resize();
        if (this.pendingPoints.length) {
          this.pendingPoints.forEach((pt) => this.pushPoint(pt));
          this.pendingPoints = [];
          this.$refs.chart?.resize();
        }
      });
    },

    buildOptions(curves) {
      const meanName = `${this.sex} Mean PFL`;
      const yMax = 32

      const asXY = (xs, ys) => xs.map((x, i) => [Number(x), Number(ys[i])]);
      const lerp = (x0, y0, x1, y1, x) =>
        x1 === x0 ? y0 : y0 + ((x - x0) * (y1 - y0)) / (x1 - x0);
      const at = (xs, ys, x) => {
        const X = xs.map(Number);
        if (x <= X[0]) return Number(ys[0]);
        if (x >= X[X.length - 1]) return Number(ys[ys.length - 1]);
        let i = 0;
        while (!(X[i] <= x && x <= X[i + 1])) i++;
        return lerp(X[i], Number(ys[i]), X[i + 1], Number(ys[i + 1]), x);
      };

      return {
        title: {
          text: `${this.sex} Palpebral Fissure Length (PFL) Over Age Range`,
          left: "center",
          top: "10px",
          textStyle: { fontSize: 16 },
        },
        grid: { height: "400px" , rught:40},
        legend: {
          data: [
            meanName,
            "90th Percentile",
            "75th Percentile",
            "25th Percentile",
            "10th Percentile",
            "Subject PFL",
          ],
          top: "30px",
        },
        tooltip: {
          trigger: "item",
          formatter: (p) => {
            const ageVal = Array.isArray(p.value)
              ? Number(p.value[0])
              : Number(p.axisValue ?? this.curves.ages[p.dataIndex]);

            const X = this.curves.ages;
            const m = at(X, this.curves.mean, ageVal);
            const p90 = at(X, this.curves.p90, ageVal);
            const p75 = at(X, this.curves.p75, ageVal);
            const p25 = at(X, this.curves.p25, ageVal);
            const p10 = at(X, this.curves.p10, ageVal);

            const meanName = `${this.sex} Mean PFL`;
            let html = '<div style="padding:5px;">';
            html += `<div style="font-weight:bold;">${meanName}: ${m.toFixed(
              2
            )}</div>`;
            html += `<div style="font-weight:bold; color:rgba(65,105,225,0.7);">90th: ${p90.toFixed(
              2
            )}</div>`;
            html += `<div style="font-weight:bold; color:rgba(60,179,113,0.7);">75th: ${p75.toFixed(
              2
            )}</div>`;
            html += `<div style="font-weight:bold; color:#FF8C00;">25th: ${p25.toFixed(
              2
            )}</div>`;
            html += `<div style="font-weight:bold; color:rgba(247,37,133,0.7);">10th: ${p10.toFixed(
              2
            )}</div>`;

            if (p.seriesName === "Subject PFL") {
              const pfl = this.pflAnalysisResult?.PFL;
              const z = this.pflAnalysisResult?.PFLzScore;
              const pct = this.pflAnalysisResult?.PFLpercentile;
              if (pfl != null) {
                html += `<div style="border:.5px solid #000; padding:8px; margin-top:10px; border-radius:5px;">
              <div style="color:#004ead; font-weight:bold;">Subject PFL: ${pfl.toFixed(
                2
              )}</div>
              <div style="color:#004ead; font-weight:bold; margin-top:5px;">Z-score: ${z?.toFixed(
                2
              )}</div>
              <div style="color:#004ead; font-weight:bold; margin-top:5px;">Percentile: ${pct?.toFixed(
                2
              )}%</div>
            </div>`;
              }
            }
            return html + "</div>";
          },
        },
        xAxis: {
          type: "value",
          min: curves.ages[0],
          max: curves.ages[curves.ages.length - 1] + 0.1,
          name: "Age",
          interval: 1,
          nameLocation: "middle",
          nameGap: 20,
          boundaryGap: false,
          axisLine: { lineStyle: { color: "#ddd" } },
          axisLabel: { color: "#333" },
        },
        yAxis: {
          type: "value",
          name: "Mean PFL",
          min: 20,
          max: yMax,
          interval: 2,
          nameGap: 40,
          nameTextStyle: { color: "#555", fontSize: 14, fontWeight: "bold" },
          axisLine: { lineStyle: { color: "#ddd" } },
          axisLabel: { color: "#333" },
        },
        series: [
          {
            name: meanName,
            type: "line",
            data: asXY(curves.ages, curves.mean),
            smooth: false,
            lineStyle: { color: "rgba(0,0,0,0.7)" },
            itemStyle: { color: "rgba(0,0,0,0.7)" },
          },
          {
            name: "90th Percentile",
            type: "line",
            data: asXY(curves.ages, curves.p90),
            smooth: false,
            lineStyle: { type: "dashed", color: "rgba(65,105,225,0.7)" },
            showSymbol: false,
            itemStyle: { color: "rgba(65,105,225,0.7)" },
          },
          {
            name: "75th Percentile",
            type: "line",
            data: asXY(curves.ages, curves.p75),
            smooth: false,
            lineStyle: { type: "dashed", color: "rgba(60,179,113,0.7)" },
            showSymbol: false,
            itemStyle: { color: "rgba(60,179,113,0.7)" },
          },
          {
            name: "25th Percentile",
            type: "line",
            data: asXY(curves.ages, curves.p25),
            smooth: false,
            lineStyle: { type: "dashed", color: "#FF8C00" },
            showSymbol: false,
            itemStyle: { color: "#FF8C00" },
          },
          {
            name: "10th Percentile",
            type: "line",
            data: asXY(curves.ages, curves.p10),
            smooth: false,
            lineStyle: { type: "dashed", color: "rgba(247,37,133,0.7)" },
            showSymbol: false,
            itemStyle: { color: "rgba(247,37,133,0.7)" },
          },
          {
            name: "Subject PFL",
            type: "scatter",
            data: [],
            symbol: `image://${crossUrl}`,
            symbolSize: 15,
            clip:false,
            itemStyle: {
              color:
                (this.sex || "").toLowerCase() === "male" ? "#7C00FE" : "red",
            },
          },
        ],
      };
    },
    pushPoint([age, pfl]) {
      if (!this.pflChart) return;
      const a = Number(age) > 16 ? 15.9 : Number(age); 
      this.pflChart.series[5].data.push([a, Number(pfl)]);
    },

    addPoint([age, pfl]) {
      if (!this.pflChart) {
        this.pendingPoints.push([age, pfl]);
        return;
      }
      this.pushPoint([age, pfl]);
      this.$nextTick(() => this.$refs.chart?.resize());
    },
  },

  mounted() {
    this.loadPFLCurves().catch(console.error);
  },
  watch: {
    sex() {
      this.chartVisible = false;
      this.pflChart = null;
      this.curves = { ages: [], mean: [], p10: [], p25: [], p75: [], p90: [] };
      this.pendingPoints = [];
      this.loadPFLCurves().catch(console.error);
    },
  },
};
</script>

<style>
.chart {
  width: 100%;
  height: 530px;
}
</style>
